import { LoadStateServerMessage } from "../types";

/**
 * Manages the loading state of an iframe and communicates with it via postMessage.
 */
export class LoadManager {
  private _iframe: HTMLIFrameElement;
  private _iframeUrl: URL;
  private _loadListener: ((data: LoadStateServerMessage) => void) | null;
  private _interactedListener: ((data: LoadStateServerMessage) => void) | null;
  private _skipLoadCheck: boolean;
  public loadState: LoadStateServerMessage["state"];

  /**
   * Creates an instance of LoadManager.
   *
   * @param {HTMLIFrameElement} iframe - The target iframe element to manage.
   */
  constructor(iframe: typeof this._iframe, skipLoadCheck?: boolean) {
    this.loadState = "loading";
    this._iframe = iframe;
    this._iframeUrl = new URL(iframe.src);
    this._loadListener = null;
    this._interactedListener = null;
    this._skipLoadCheck = skipLoadCheck ?? false;
  }

  private _isLoadStateServerMessage(data: LoadStateServerMessage) {
    return data && typeof data === "object" && data.type === "loadState";
  }

  private _processData(data: LoadStateServerMessage) {
    const isLoadStateServerMessage = this._isLoadStateServerMessage(data);
    if (!isLoadStateServerMessage) {
      return {
        isLoadStateServerMessage,
        isReady: false,
        isError: false,
        hasInteracted: false,
      };
    }
    this.loadState = data.state;
    const isReady = data.state === "ready";
    const isError = data.state === "loadError";
    const hasInteracted = "hasInteracted" in data && data.hasInteracted;
    return {
      isLoadStateServerMessage,
      isReady,
      isError,
      hasInteracted,
    };
  }

  /**
   * Initializes the LoadManager by listening for a "loadState" message from the iframe.
   * Resolves when the iframe reports that it's in a "ready" state.
   *
   * @async
   * @returns {Promise<{ready: boolean, state: LoadStateServerMessage["state"] | "timeout", hasInteractedPromise: Promise<boolean>}>}
   *
   *  [loadState]
   *
   *  A promise that resolves with `{ready: true, state: 'ready'}` when the iframe is ready.
   *  If there is no answer after 10 seconds, it will resolve with `{ready: false, state: 'timeout'}`.
   *  If there is a loadError, it resolves with `{ready: false, state: 'loadError'}`
   *
   *  [interactionState]
   *  Also gives you a promise once the user interacts with the document. This promise needs to be fulfilled
   *  before audio can be played through the iframe.
   *  If a loadError occurs, the Promise will reject.
   *  If a timeout occurs, the Promise will still behave like normal.
   *
   */
  async init(): Promise<{
    ready: boolean;
    state: LoadStateServerMessage["state"] | "timeout";
    hasInteracted: LoadStateServerMessage["hasInteracted"];
    hasInteractedPromise: Promise<boolean>;
  }> {
    console.log("Initializing Sandai Load Manager...");
    if (this._skipLoadCheck) {
      console.warn(
        "[sandai-core] [LoadManager] The load check was skipped.\n" +
          "The load check should be enabled in prod so you can debug load-related issues " +
          "like a faulty vrmUrl or similar.\n" +
          "You should also rely on knowing if a user has interacted with the iframe for " +
          "making sure that audio playback is allowed, which is also handled by the load manager, " +
          "so if your model is not talking, make sure to interact with the iframe first",
      );
      return {
        ready: true,
        state: "ready",
        hasInteracted: true,
        hasInteractedPromise: Promise.resolve(true),
      };
    }

    const hasInteractedPromise = new Promise<boolean>((resolve, reject) => {
      this._interactedListener = ((data: LoadStateServerMessage) => {
        const state = this._processData(data);
        if (!state.isLoadStateServerMessage || !state.hasInteracted) return;
        if (state.isReady && state.hasInteracted) {
          console.log("[sandai-core] User interacted with Sandai!");
          resolve(true);
          this.destroyInteractionStateListeners();
        } else if (state.isError) {
          reject(
            "An error occured while loading in the load step. " +
              "The Interaction State cannot be determined",
          );
          this.destroyInteractionStateListeners();
        }
      }).bind(this);
      this._listenForMessage(this._interactedListener, "interactionState");
    });

    const interrimLoadState = { hasLoaded: false };
    const loadPromise = new Promise<Awaited<ReturnType<typeof this.init>>>(
      (resolve) => {
        this._loadListener = ((data: LoadStateServerMessage) => {
          const state = this._processData(data);
          if (!state.isLoadStateServerMessage) return;
          if (state.isReady) {
            console.log("[sandai-core] Initialized Sandai Load Manager!");
            console.log("[sandai-core] Sandai iframe loaded!");
            interrimLoadState.hasLoaded = true;
            resolve({
              ready: true,
              state: data.state,
              hasInteracted: data.hasInteracted,
              hasInteractedPromise,
            });
            this.destroyLoadStateListeners();
          } else if (state.isError) {
            console.error(
              "[sandai-core] Failed to initialize Sandai client. Error was:",
              data.message,
            );
            resolve({
              ready: false,
              state: data.state,
              hasInteracted: true,
              hasInteractedPromise: Promise.reject(
                "An error occured while loading in the load step. " +
                  "The Interaction State cannot be determined. " +
                  "The hasInteracted state is set to true to avoid any issues just in case. ",
              ),
            });
            this.destroyLoadStateListeners();
          }
        }).bind(this);
        this._listenForMessage(this._loadListener, "loadState");
      },
    );
    const timeoutPromise = new Promise<Awaited<ReturnType<typeof this.init>>>(
      (resolve) => {
        setTimeout(() => {
          resolve({
            ready: interrimLoadState.hasLoaded,
            state: interrimLoadState.hasLoaded ? "ready" : "timeout",
            hasInteracted: true,
            hasInteractedPromise: interrimLoadState.hasLoaded
              ? hasInteractedPromise
              : Promise.reject(
                  "The load step timed out. " +
                    "The Interaction State cannot be determined. " +
                    "The hasInteracted state is set to true to avoid any issues just in case. ",
                ),
          });
        }, 20000);
      },
    );
    const loadRaceResult = await Promise.race([loadPromise, timeoutPromise]);
    if (loadRaceResult.state === "timeout") {
      console.warn(
        "[sandai-core] [LoadManager] The load manager didn't receive a message after 10 seconds.\n" +
          "Something is really wrong here, but it might work just fine. If you see this and things aren't fine, " +
          "reach out to info@sandai.org",
      );
    }
    return loadRaceResult;
  }

  /**
   * Sends a message to the iframe.
   *
   * @private
   * @param {*} data - The data payload to be sent to the iframe.
   * @throws {Error} Throws an error if the iframe does not have a `contentWindow`.
   */
  _sendMessage(data: any) {
    if (this._iframe.contentWindow) {
      this._iframe.contentWindow.postMessage(data, "*");
    } else {
      throw new Error("iframe doesn't have a 'contentWindow' member");
    }
  }

  /**
   * Stores all registered message event listeners for later removal.
   *
   * @private
   */
  private _listeners: Array<{
    listener: (event: MessageEvent<any>) => void;
    type: "interactionState" | "loadState";
  }> = [];

  /**
   * Listens for messages from the iframe and invokes the provided callback when a message is received.
   * Only messages from the iframe's origin are processed.
   *
   * The listener is stored internally so it can be removed later via `destroy()`.
   *
   * @private
   * @param {(data: any) => void} callback - The callback function to invoke with the message data.
   */
  private _listenForMessage(
    callback: (data: any) => void,
    type: (typeof this._listeners)[number]["type"],
  ) {
    const listener = ((event: MessageEvent<any>) => {
      if (event.origin !== this._iframeUrl.origin) return;
      callback(event.data);
    }).bind(this);
    this._listeners.push({ listener, type });
    window.addEventListener("message", listener);
  }

  destroyLoadStateListeners() {
    console.log("Destroying Load State Listeners...");
    this._listeners.forEach((listener) => {
      if (listener.type === "loadState") {
        window.removeEventListener("message", listener.listener);
      }
    });
    this._listeners = this._listeners.filter((l) => l.type !== "loadState");
    console.log("Destroyed Load State Listeners!");
  }

  destroyInteractionStateListeners() {
    console.log("Destroying Interaction State Listeners...");
    this._listeners.forEach((listener) => {
      if (listener.type === "interactionState") {
        window.removeEventListener("message", listener.listener);
      }
    });
    this._listeners = this._listeners.filter(
      (l) => l.type !== "interactionState",
    );
    console.log("Destroyed Interaction State Listeners!");
  }

  /**
   * Cleans up all registered message event listeners previously added by `_listenForMessage`.
   * Call this method when the instance is being destroyed or no longer needs to listen to messages.
   */
  destroy() {
    console.log("Destroying Load Manager...");
    this.destroyLoadStateListeners();
    this.destroyInteractionStateListeners();
    console.log("Destroyed Load Manager!");
  }
}
